import { type KeyboardEvent, useEffect, useRef } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";

import type { DeskPhone } from "services/dialstack";

import {
  deskPhoneActionsSx,
  deskPhoneBackButtonSx,
  deskPhoneEmbeddedBodySx,
  deskPhoneEmbeddedTitleSx,
  deskPhoneHintSx,
  deskPhoneListSx,
  deskPhonePickerEmbeddedSx,
  deskPhonePickerHeaderSx,
  deskPhonePickerSx,
  deskPhonePickerTitleSx,
  deskPhonePrimaryButtonSx,
  deskPhoneRowChevronSx,
  deskPhoneRowDetailSx,
  deskPhoneRowSx,
  deskPhoneRowTextSx,
  deskPhoneRowTitleSx,
  deskPhoneSpinnerRowSx,
  deskPhoneStateBodySx,
  deskPhoneStateStrongSx,
  deskPhoneStateTitleSx,
  deskPhoneTextButtonSx
} from "../dialstack-softphone-widget.styles";
import {
  BACK_LABEL,
  BACK_TO_LIST_LABEL,
  CANCEL_LABEL,
  CHANGED_BODY,
  DONE_BODY,
  FAILED_FALLBACK,
  LOADING_PHONES_LABEL,
  LOGGING_OUT_LABEL,
  LOG_OUT_LABEL,
  NO_PHONES_LABEL,
  PHONES_UNAVAILABLE_LABEL,
  PICKER_HINT,
  PICKER_TITLE,
  TRY_AGAIN_LABEL,
  changedTitle,
  confirmLines,
  doneTitle,
  failedTitle,
  holdersLabel,
  logInLabel,
  logInTitle,
  logOutBody,
  logOutFailedTitle,
  logOutTitle,
  loggingInTitle,
  ringsOnAfterLogOut,
  rowLabel,
  rowTitle
} from "./desk-phone-copy";
import type {
  DeskPhoneRequest,
  DeskPhonesController
} from "./use-desk-phones.hook";

type Props = {
  controller: DeskPhonesController;
  // Back to wherever the picker was opened from (the keypad, or the Settings
  // dialog). A log-in in flight is not cancelled by leaving.
  onClose: () => void;
  // Inside a dialog that carries its own title: no card chrome, no back row,
  // the dialog's larger type.
  embedded?: boolean;
};

type HeadingRef = React.RefObject<HTMLElement>;

function Heading({
  children,
  embedded,
  headingRef
}: {
  children: string;
  embedded: boolean;
  headingRef?: HeadingRef;
}) {
  return (
    <Typography
      component="h3"
      ref={headingRef}
      tabIndex={-1}
      sx={[deskPhoneStateTitleSx, embedded && deskPhoneEmbeddedTitleSx]}
    >
      {children}
    </Typography>
  );
}

function Body({
  children,
  embedded,
  strong = false,
  role
}: {
  children: string;
  embedded: boolean;
  strong?: boolean;
  role?: string;
}) {
  return (
    <Typography
      role={role}
      sx={[
        strong ? deskPhoneStateStrongSx : deskPhoneStateBodySx,
        embedded && deskPhoneEmbeddedBodySx
      ]}
    >
      {children}
    </Typography>
  );
}

function PrimaryButton({
  label,
  onClick,
  embedded
}: {
  label: string;
  onClick: () => void;
  embedded: boolean;
}) {
  return (
    <Button
      variant="contained"
      size={embedded ? "medium" : "small"}
      className="suno-phone-no-drag"
      onClick={onClick}
      sx={deskPhonePrimaryButtonSx}
    >
      {label}
    </Button>
  );
}

function TextButton({
  label,
  onClick,
  embedded
}: {
  label: string;
  onClick: () => void;
  embedded: boolean;
}) {
  return (
    <Button
      variant="text"
      size={embedded ? "medium" : "small"}
      className="suno-phone-no-drag"
      onClick={onClick}
      sx={deskPhoneTextButtonSx}
    >
      {label}
    </Button>
  );
}

function Working({
  label,
  embedded,
  headingRef
}: {
  label: string;
  embedded: boolean;
  headingRef: HeadingRef;
}) {
  return (
    <Box role="status" sx={deskPhoneSpinnerRowSx}>
      <CircularProgress aria-hidden color="inherit" size={14} />
      <Heading embedded={embedded} headingRef={headingRef}>
        {label}
      </Heading>
    </Box>
  );
}

function PhoneList({
  phones,
  isLoading,
  isError,
  embedded,
  onSelect,
  onRetry
}: {
  phones: DeskPhone[];
  isLoading: boolean;
  isError: boolean;
  embedded: boolean;
  onSelect: (phone: DeskPhone) => void;
  onRetry: () => void;
}) {
  if (isLoading)
    return (
      <Box sx={deskPhoneSpinnerRowSx} role="status">
        <CircularProgress aria-hidden color="inherit" size={14} />
        <Body embedded={embedded}>{LOADING_PHONES_LABEL}</Body>
      </Box>
    );
  if (isError)
    return (
      <>
        <Body embedded={embedded} role="alert">
          {PHONES_UNAVAILABLE_LABEL}
        </Body>
        <Box sx={deskPhoneActionsSx}>
          <TextButton
            label={TRY_AGAIN_LABEL}
            onClick={onRetry}
            embedded={embedded}
          />
        </Box>
      </>
    );
  if (phones.length === 0)
    return <Body embedded={embedded}>{NO_PHONES_LABEL}</Body>;
  return (
    <List aria-label={PICKER_TITLE} sx={deskPhoneListSx}>
      {phones.map((phone) => (
        <ListItem key={phone.id} disablePadding>
          <ListItemButton
            className="suno-phone-no-drag"
            disabled={phone.mine}
            aria-label={rowLabel(phone)}
            onClick={() => onSelect(phone)}
            sx={deskPhoneRowSx}
            data-testid={`desk-phone-row-${phone.id}`}
          >
            <Box sx={deskPhoneRowTextSx}>
              <Typography
                component="span"
                display="block"
                sx={[deskPhoneRowTitleSx, embedded && deskPhoneEmbeddedTitleSx]}
              >
                {rowTitle(phone)}
              </Typography>
              <Typography
                component="span"
                display="block"
                sx={[deskPhoneRowDetailSx, embedded && deskPhoneEmbeddedBodySx]}
              >
                {holdersLabel(phone)}
              </Typography>
            </Box>
            {!phone.mine && (
              <ChevronRightIcon aria-hidden sx={deskPhoneRowChevronSx} />
            )}
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}

// One phone's log-in, from "Log in on Front desk?" to "Front desk is yours." —
// or its log-out, or how either failed. One screen per phase so the person
// always has exactly the actions that apply. The confirm reads the phone's
// LIVE row (whose it is, who is on a call), not a snapshot from when it was
// chosen.
function RequestState({
  controller,
  onClose,
  embedded,
  headingRef
}: {
  controller: DeskPhonesController;
  onClose: () => void;
  embedded: boolean;
  headingRef: HeadingRef;
}) {
  const { request, phones, me, mySession, confirm, cancel, confirmLogOut } =
    controller;
  if (!request) return null;
  const phone =
    phones.find((candidate) => candidate.id === request.phone.id) ??
    request.phone;
  const { error } = request;
  const leave = () => {
    cancel();
    onClose();
  };
  switch (request.phase) {
    case "confirming":
      return (
        <>
          <Heading embedded={embedded} headingRef={headingRef}>
            {logInTitle(phone.name)}
          </Heading>
          {confirmLines(phone, me, phones).map((line) => (
            <Body key={line.text} embedded={embedded} strong={line.strong}>
              {line.text}
            </Body>
          ))}
          <Box sx={deskPhoneActionsSx}>
            <PrimaryButton
              label={logInLabel(phone)}
              onClick={confirm}
              embedded={embedded}
            />
            <TextButton
              label={CANCEL_LABEL}
              onClick={cancel}
              embedded={embedded}
            />
          </Box>
        </>
      );
    case "loggingIn":
      return (
        <Working
          label={loggingInTitle(phone.name)}
          embedded={embedded}
          headingRef={headingRef}
        />
      );
    case "done":
      return (
        <Box role="status">
          <Heading embedded={embedded} headingRef={headingRef}>
            {doneTitle(phone.name)}
          </Heading>
          <Body embedded={embedded}>{DONE_BODY}</Body>
        </Box>
      );
    case "changed":
      return (
        <>
          <Heading embedded={embedded} headingRef={headingRef}>
            {changedTitle(phone.name)}
          </Heading>
          <Body embedded={embedded} role="alert">
            {CHANGED_BODY}
          </Body>
          <Box sx={deskPhoneActionsSx}>
            <PrimaryButton
              label={BACK_TO_LIST_LABEL}
              onClick={cancel}
              embedded={embedded}
            />
          </Box>
        </>
      );
    case "failed":
      return (
        <>
          <Heading embedded={embedded} headingRef={headingRef}>
            {failedTitle(phone.name)}
          </Heading>
          <Body embedded={embedded} role="alert">
            {error ?? FAILED_FALLBACK}
          </Body>
          <Box sx={deskPhoneActionsSx}>
            <PrimaryButton
              label={TRY_AGAIN_LABEL}
              onClick={confirm}
              embedded={embedded}
            />
            <TextButton
              label={CANCEL_LABEL}
              onClick={cancel}
              embedded={embedded}
            />
          </Box>
        </>
      );
    case "confirmingLogOut":
      return (
        <>
          <Heading embedded={embedded} headingRef={headingRef}>
            {logOutTitle(phone.name)}
          </Heading>
          {mySession && (
            <Body embedded={embedded}>
              {logOutBody(
                mySession,
                phones.find((candidate) => candidate.id === phone.id) ?? null,
                ringsOnAfterLogOut(me, phones)
              )}
            </Body>
          )}
          <Box sx={deskPhoneActionsSx}>
            <PrimaryButton
              label={LOG_OUT_LABEL}
              onClick={confirmLogOut}
              embedded={embedded}
            />
            <TextButton
              label={CANCEL_LABEL}
              onClick={leave}
              embedded={embedded}
            />
          </Box>
        </>
      );
    case "loggingOut":
      return (
        <Working
          label={LOGGING_OUT_LABEL}
          embedded={embedded}
          headingRef={headingRef}
        />
      );
    case "logOutFailed":
      return (
        <>
          <Heading embedded={embedded} headingRef={headingRef}>
            {logOutFailedTitle(phone.name)}
          </Heading>
          <Body embedded={embedded} role="alert">
            {error ?? FAILED_FALLBACK}
          </Body>
          <Box sx={deskPhoneActionsSx}>
            <PrimaryButton
              label={TRY_AGAIN_LABEL}
              onClick={confirmLogOut}
              embedded={embedded}
            />
            <TextButton
              label={CANCEL_LABEL}
              onClick={leave}
              embedded={embedded}
            />
          </Box>
        </>
      );
    default:
      return null;
  }
}

// The in-panel desk-phone picker (ENG-2293): every desk phone in the practice
// with whose it is, in words; then the log-in on one of them, or the log-out.
// Rendered in place of the dial pad while open, or inside a Settings dialog.
// Keyboard: focus lands on the heading of whatever screen just appeared,
// Escape steps back (confirm → list → keypad), and nothing typed here reaches
// the dial pad.
export default function DeskPhonePicker({
  controller,
  onClose,
  embedded = false
}: Props) {
  const { phones, isLoading, isError, request, select, cancel, refetch } =
    controller;
  const phase = request?.phase ?? "list";
  const inFlight = phase === "loggingIn" || phase === "loggingOut";
  const headingRef = useRef<HTMLElement>(null);

  // Focus follows the phase: the heading of the screen that just appeared.
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [phase]);

  // A finished log-in ("… is yours." has lingered) or a finished log-out hands
  // the panel back by itself; a cancel from the confirm stays on the list.
  const previousPhase = useRef<DeskPhoneRequest["phase"] | null>(null);
  useEffect(() => {
    const was = previousPhase.current;
    previousPhase.current = request?.phase ?? null;
    if (!request && (was === "done" || was === "loggingOut")) onClose();
  }, [request, onClose]);

  const back = () => {
    if (!inFlight) cancel();
    onClose();
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    if (!request || inFlight) onClose();
    else cancel();
  };

  return (
    <Box
      sx={embedded ? deskPhonePickerEmbeddedSx : deskPhonePickerSx}
      onKeyDown={handleKeyDown}
      data-testid="desk-phone-picker"
    >
      {!embedded && (
        <Box sx={deskPhonePickerHeaderSx}>
          <IconButton
            aria-label={BACK_LABEL}
            className="suno-phone-no-drag"
            size="small"
            onClick={back}
            sx={deskPhoneBackButtonSx}
          >
            <ArrowBackIcon fontSize="inherit" />
          </IconButton>
          <Typography
            component="h2"
            ref={phase === "list" ? headingRef : undefined}
            tabIndex={-1}
            sx={deskPhonePickerTitleSx}
          >
            {PICKER_TITLE}
          </Typography>
        </Box>
      )}
      {phase === "list" && (
        <Typography
          ref={embedded ? headingRef : undefined}
          tabIndex={embedded ? -1 : undefined}
          sx={[deskPhoneHintSx, embedded && deskPhoneEmbeddedBodySx]}
        >
          {PICKER_HINT}
        </Typography>
      )}
      {request ? (
        <RequestState
          controller={controller}
          onClose={onClose}
          embedded={embedded}
          headingRef={headingRef}
        />
      ) : (
        <PhoneList
          phones={phones}
          isLoading={isLoading}
          isError={isError}
          embedded={embedded}
          onSelect={select}
          onRetry={refetch}
        />
      )}
    </Box>
  );
}
